/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { entities } from '../../../constants/entities'
import { formatDateFront } from '../../../utils/formatDate'
import useAction from '../../../hooks/useAction'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import { faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import useSelectOptions from '../../../hooks/useSelectOptions'
import { useOrgan } from '../../../hooks/useOrgan'
import { useReport } from '../../../context/ReportContext'

const EquipmentByCustomer = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { selectedOrgan } = useOrgan()

  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  // Se ao menos um tiver preenchido marcar como true
  const [hasFilled, setHasFilled] = useState(false)

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      customer_id: formData.filter.customer_id?.value,
      recipient_customers_ids: formData.filter.recipient_customers_ids?.map(
        (recipient) => recipient.value
      ),
      // * Se só o cliente tiver selecionado, carregar os destinos desse cliente, se não, somente do cliente destinatário
      ...(formData.filter.recipient_customers_ids?.length === 0 && {
        address_ids: formData.filter.customer_id
          ? [formData.filter.customer_id.value]
          : (formData.filter.address_ids?.map((id) => id.value) ?? []),
      }),
      ...(formData.filter.with_maintenances?.value === true && {
        with_maintenances: formData.filter.with_maintenances?.value,
      }),
      ...(formData.filter.with_reserved?.value === true && {
        with_reserved: formData.filter.with_reserved?.value,
      }),
      ...(formData.filter.with_others?.value === true && {
        with_others: formData.filter.with_others?.value,
      }),
      organization_id: selectedOrgan?.id,
      deleted_at: false,
    },
    'equipmentsByCustomer'
  )

  const customerIdRef = useRef(formData.filter.customer_id?.value)
  const recipientCustomersIdsRef = useRef(
    formData.filter.recipient_customers_ids?.map((recipient) => recipient.value)
  )
  const addressIdsRef = useRef(
    formData.filter.address_ids?.map((address) => address.value)
  )

  // * Se o cliente tiver mudado, limpar os recipient_customers_ids e address_ids
  useEffect(() => {
    customerIdRef.current = formData.filter.customer_id?.value
    recipientCustomersIdsRef.current =
      formData.filter.recipient_customers_ids?.map(
        (recipient) => recipient.value
      )
    addressIdsRef.current = formData.filter.address_ids?.map((id) => id.value)
  }, [formData])

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  // Cliente contratante
  const customerSelect = useRemoteSelectOptions({
    queryKey: ['customers'],
    baseServiceGet,
    entityPath: entities.customers.get,
    extraFilters: {
      alias: { $nin: ['ALUCOM', 'MOREIA', 'IP', 'AS SISTEMAS'] },
    },
    columnDataBase: ['id', 'alias'],
    fieldId: 'filter.customer_id',
    formData,
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    refetchOnWindowFocus: true,
  })

  const recipientCustomerSelect = useRemoteSelectOptions({
    queryKey: ['recipientCustomerOptions'],
    baseServiceGet,
    entityPath: entities.customers.get,
    extraFilters: {
      alias: { $nin: ['ALUCOM', 'MOREIA', 'IP', 'AS SISTEMAS'] },
      parent_id: { $eq: customerSelect.selectedValue?.value },
    },
    columnDataBase: ['id', 'alias'],
    fieldId: 'filter.recipient_customers_ids',
    formData,
    isMulti: true,
    enabled: !!customerSelect.selectedValue?.value,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias}`,
    }),
    setFormData,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    refetchOnWindowFocus: true,
  })

  const equipmentsInReservedSelect = useSelectOptions({
    fieldId: 'filter.with_reserved',
    formData,
    isMulti: false,
    setFormData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Nao' },
    ],
  })

  const equipmentsWithOthersSelect = useSelectOptions({
    fieldId: 'filter.with_others',
    formData,
    isMulti: false,
    setFormData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Nao' },
    ],
  })

  const equipmentsInMaintenanceSelect = useSelectOptions({
    fieldId: 'filter.with_maintenances',
    formData,
    isMulti: false,
    setFormData,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
    rawOptions: [
      { value: true, label: 'Sim' },
      { value: false, label: 'Nao' },
    ],
  })

  useEffect(() => {
    // Por padrão é não
    setFormData((prev) => ({
      filter: {
        ...prev.filter,
        with_reserved: { value: false, label: 'Nao' },
        with_others: { value: false, label: 'Nao' },
        with_maintenances: { value: false, label: 'Nao' },
      },
    }))
  }, [])

  const addressSelect = useRemoteSelectOptions({
    queryKey: [
      'address',
      recipientCustomerSelect.selectedValue,
      customerSelect.selectedValue,
    ],
    fieldId: 'filter.address_ids',
    baseServiceGet,
    entityPath: entities.addresses.get,
    columnDataBase: ['id', 'name'],
    extraFilters: {
      addressable_id: {
        $in:
          recipientCustomerSelect.selectedValue.length > 0
            ? recipientCustomerSelect.selectedValue.map((c) => c.value)
            : customerSelect.selectedValue
              ? [customerSelect.selectedValue.value]
              : [],
      },
      addressable_type: 'customer',
    },
    formData,
    setFormData,
    isMulti: true,
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.formatted_address}`,
    }),
    enabled:
      recipientCustomerSelect.selectedValue.length > 0 ||
      customerSelect.selectedValue,
    initialPage: 1,
    perPage: 15,
    enablePagination: true,
    refetchOnWindowFocus: true,
    refetchWhen: () => {
      if (customerIdRef.current !== customerSelect.selectedValue?.value) {
        customerIdRef.current = customerSelect.selectedValue?.value
        return true
      }
      if (
        recipientCustomersIdsRef.current?.toString() !==
        recipientCustomerSelect.selectedValue.map((c) => c.value).toString()
      ) {
        recipientCustomersIdsRef.current =
          recipientCustomerSelect.selectedValue.map((c) => c.value)
        return true
      }
      return false
    },
  })

  const selects = {
    'filter.customer_id': customerSelect,
    'filter.recipient_customers_ids': recipientCustomerSelect,
    'filter.address_ids': addressSelect,
    'filter.with_maintenances': equipmentsInMaintenanceSelect,
    'filter.with_reserved': equipmentsInReservedSelect,
    'filter.with_others': equipmentsWithOthersSelect,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  // Equipamentos a partir do cliente contratante, mais também a dos seus subclientes
  const {
    data: equipmentsByCustomerData,
    isError,
    error,
    isLoading,
    isFetching,
  } = useQueryWithLoader({
    queryKey: ['equipmentsByCustomer', filters, pagination],
    queryFn: async () => {
      const params = {
        filters,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: {
          column: 'number',
          order: 'desc',
        },
      }
      const response = await baseServiceGet(
        entities.equipments.byCustomer.get,
        params
      )
      return response.result
    },
    staleTime: 10000,
    enabled: hasFilled,
    select: (data) => {
      return {
        equipmentsByCustomer: data?.data?.map((equipment) => ({
          id: equipment.id,
          equipment: `${equipment.number} - ${equipment.name}`,
          situation: equipment.status?.name ?? '-',
          // * Isso é devido a equipamentos podendo virem tanto dos movimentos tanto das ordem de serviço, devido ao status de "EM DEVOLUÇÃO"
          movement_date: equipment.last_relevant_movement_item
            ? formatDateFront({
                rawDate:
                  equipment.last_relevant_movement_item?.movement
                    ?.movement_date,
              })
            : equipment.last_relevant_service_order_item
              ? formatDateFront({
                  rawDate:
                    equipment.last_relevant_service_order_item?.order
                      ?.created_at,
                })
              : '-',
        })),
        totalPages: data.last_page,
      }
    },

    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    const hasFilledValidate = !!formData.filter.customer_id?.value // só considera o cliente

    setHasFilled(hasFilledValidate)
  }, [formData])

  // Sempre que o formData mudar, sincronizamos com a URL para disparar a Query
  useEffect(() => {
    const newFilters = {
      ...filters,
      customer_id: formData.filter.customer_id?.value,
      recipient_customers_ids:
        formData.filter.recipient_customers_ids?.map((r) => r.value) || [],
      address_ids: formData.filter.address_ids?.map((a) => a.value) || [],
      with_maintenances: formData.filter.with_maintenances?.value || false,
      with_reserved: formData.filter.with_reserved?.value || false,
      with_others: formData.filter.with_others?.value || false,
    }

    // applyUrlFilters atualiza a URL e o objeto 'filters' usado na queryKey
    applyUrlFilters(newFilters)
  }, [
    formData.filter.customer_id,
    formData.filter.recipient_customers_ids,
    formData.filter.address_ids,
    formData.filter.with_maintenances,
    formData.filter.with_reserved,
    formData.filter.with_others,
  ])

  //! useEffect para limpar os campos dependentes (sub-cliente e destino) quando trocar o cliente
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      filter: {
        ...prev.filter,
        recipient_customers_ids: [],
        address_ids: [],
      },
    }))
  }, [formData.filter.customer_id?.value])

  //! useEffect para remover o destino do respectivo sub-cliente quando trocar o sub-cliente
  useEffect(() => {
    // Pegamos os IDs dos sub-clientes que restaram no select
    const currentRecipientIds =
      formData.filter.recipient_customers_ids?.map((r) => r.value) || []

    setFormData((prev) => {
      // Filtramos a lista de destinos para remover aqueles que pertenciam ao sub-cliente removido
      const updatedDestinations =
        prev.filter.address_ids?.filter((destination) => {
          // Aqui precisamos saber a qual sub-cliente esse destino pertence.
          // Se o seu objeto de opção de destino não tiver essa info,
          // podemos buscar no meta ou nos itens carregados,
          // mas o ideal é que o 'value' ou um campo extra no option ajude.

          // Assumindo que você queira limpar endereços que não batam com os IDs atuais:
          // Se não houver sub-clientes, e o destino for do cliente principal, mantemos.
          // Caso contrário, verificamos se o vínculo ainda existe.

          // Uma implementação segura é verificar se o destino ainda é válido para os sub-clientes ativos:
          return currentRecipientIds.includes(destination.customer_id) // Ajuste 'customer_id' para a chave correta no seu objeto de destino
        }) || []

      // Se o número de endereços mudou, atualizamos o estado
      if (
        updatedDestinations.length !== (prev.filter.address_ids?.length || 0)
      ) {
        return {
          ...prev,
          filter: {
            ...prev.filter,
            address_ids: updatedDestinations,
          },
        }
      }
      return prev
    })
  }, [formData.filter.recipient_customers_ids])

  useEffect(() => {
    if (isLoading || isFetching) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoading, isFetching])

  const { equipmentsByCustomer = [], totalPages = 1 } =
    equipmentsByCustomerData || {}

  // Limpa o data se nenhum filtro tiver selecionado
  useEffect(() => {
    if (!hasFilled) {
      queryClient.setQueryData(['equipmentsByCustomer', filters, pagination], {
        equipmentsByCustomer: [],
        totalPages: 1,
      })
    }
  }, [hasFilled])

  const columns = [
    { key: 'equipment', label: 'Equipamento', width: '60%' },
    { key: 'situation', label: 'Situação' },
    { key: 'movement_date', label: 'Data do Movimento' },
  ]

  useEffect(() => {
    if (isError) {
      showNotification(
        'error',
        `Erro ao carregar faturamentos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()

      const params = { filters, sort: { column: 'number', order: 'desc' } }
      const response = await baseServiceGet(
        entities.reports.equipmentByCustomer.get,
        params,
        {
          type,
        }
      )
      return response
    },
    onMutate: () => {
      showLoader()
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const {
    openModalConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Equipamentos por cliente" showBackButton={false} />
      <Alert
        severity="info"
        sx={{
          mb: 2,
        }}>
        <strong>Atenção:</strong> Esse relatório busca todos os equipamentos do
        cliente, ignorando contratos e movimentos avulsos realizados.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />

                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={
                    !hasFilled ||
                    equipmentsByCustomer?.length === 0 ||
                    equipmentsWithOthersSelect.selectedValue?.value === true ||
                    isExporting
                  }
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFileExcel} />}
                  text="Exportar em EXCEL"
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('excel')
                    } catch (error) {
                      console.error('Erro ao exportar EXCEL:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                  disabled={
                    !hasFilled ||
                    equipmentsByCustomer?.length === 0 ||
                    equipmentsWithOthersSelect.selectedValue?.value === true ||
                    isExporting
                  }
                />
              </Box>
            </>
          )}
        </Form>

        <TableWithInactiveFilter
          data={equipmentsByCustomer}
          columns={columns}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          applyUrlFilters={applyUrlFilters}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          type="default"
          showFilterInactive={false}
          isLoading={isLoading}
          isFetching={isFetching || exportReport.isPending}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={
            `N° do Movimento (${selectedItem?.number}) - Cliente: ${selectedItem?.customer}` ||
            ''
          }
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default EquipmentByCustomer
